/**
 * Modal de déplacement d'un créneau EDT (jour + heure).
 * Si tronc commun : option de propager à toutes les classes du groupe.
 */
import { useState } from 'react';
import { X, Move, Loader2, Layers } from 'lucide-react';
import { useDeplacerEdtMutation } from '../../services/vieScolaireApi';

const JOURS_LABEL: Record<string, string> = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi',
  jeudi: 'Jeudi', vendredi: 'Vendredi', samedi: 'Samedi',
};

export default function ModalDeplacer({
  source, creneauxStd, onClose, onMoved,
}: {
  source: any;
  creneauxStd: Array<{ heure_debut: string; heure_fin: string }>;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [jour, setJour] = useState(source.jour);
  const [hd, setHd] = useState(source.heure_debut);
  const [hf, setHf] = useState(source.heure_fin);
  const [salle, setSalle] = useState(source.salle || '');
  const [propager, setPropager] = useState(true);
  const [deplacer, { isLoading }] = useDeplacerEdtMutation();

  const choisirCreneauStd = (h_debut: string, h_fin: string) => {
    setHd(h_debut); setHf(h_fin);
  };

  const handleSubmit = async () => {
    try {
      const res = await deplacer({
        edtId: source.id,
        jour, heure_debut: hd, heure_fin: hf, salle,
        propager: !!source.tronc_commun_groupe && propager,
      }).unwrap();
      const nb = res.nb_creneaux_modifies || 0;
      if (res.propage && nb > 1) {
        alert(`Déplacement effectué pour ${nb} classes (tronc commun).`);
      }
      onMoved();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white dark:bg-gray-900 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
          <div>
            <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Move size={16} /> Déplacer le créneau
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {source.libelle_matiere} • {source.classe}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Actuellement : {JOURS_LABEL[source.jour]} {source.heure_debut}–{source.heure_fin}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Nouveau jour</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(JOURS_LABEL).map(([v, l]) => (
                <button key={v} onClick={() => setJour(v)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border ${
                    jour === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Nouvel horaire</label>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {creneauxStd.map((c, idx) => {
                const actif = hd === c.heure_debut && hf === c.heure_fin;
                return (
                  <button key={idx} onClick={() => choisirCreneauStd(c.heure_debut, c.heure_fin)}
                    className={`px-2 py-1.5 text-xs rounded-lg border ${
                      actif
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                    }`}>
                    {c.heure_debut} – {c.heure_fin}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 items-center text-xs">
              <input type="time" value={hd} onChange={(e) => setHd(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
              <span className="text-gray-500">à</span>
              <input type="time" value={hf} onChange={(e) => setHf(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Salle (optionnel)</label>
            <input value={salle} onChange={(e) => setSalle(e.target.value)}
              maxLength={50} placeholder="Ex. B12"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
          </div>

          {source.tronc_commun_groupe && (
            <label className="flex items-start gap-2 cursor-pointer p-2.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <input type="checkbox" checked={propager} onChange={(e) => setPropager(e.target.checked)}
                className="mt-0.5" />
              <div className="text-xs text-purple-700 dark:text-purple-300">
                <span className="font-semibold inline-flex items-center gap-1"><Layers size={11} /> Tronc commun</span>
                <p className="mt-0.5">Déplacer aussi pour toutes les autres classes qui suivent ce cours en commun.</p>
              </div>
            </label>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg">
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={isLoading}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center gap-2">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Move size={14} />}
            Déplacer
          </button>
        </footer>
      </div>
    </div>
  );
}
